import { computed, type ComputedRef, type Ref } from 'vue'
import type { Props } from './ReadyToGoRings.vue'
import { useFiltersCount } from '../useFiltersCount'
import { type ReadyToGoRingsRoute, useRoutes } from '@/router/routes'
import type {
    CatalogueReadyToGoRingsFilterInput,
    CatalogueSortOrder,
    MetalCode,
    ReadyToGoRingFiltersFragment,
} from '@/graphql/api'
import { useCountryPlugin } from '@/plugins/country.ts'
import type { FilterChip } from '@/components/ui/filter/FilterChips.vue'
import { useFormattedPriceRange } from '@/composables/useMoneyFormatter'
import { systemCodeToWords } from '@/helpers'
import type { InputRangeModel } from '@/components/ui/inputs/range/input-range-two-knobbed-props.ts'
import { useCurrencyInput } from '@/plugins/currency-input.ts'

export interface ReadyToGoRingsFilters {
    filterInput: ComputedRef<CatalogueReadyToGoRingsFilterInput>
    routeReset: ReadyToGoRingsRoute
    getRoutePrice(values: InputRangeModel): ReadyToGoRingsRoute
    getRouteAddMetal(metal: MetalCode): ReadyToGoRingsRoute
    getRouteRemoveMetal(): ReadyToGoRingsRoute
    getRouteSort(sortBy: CatalogueSortOrder): ReadyToGoRingsRoute
    activeFilterCount: ComputedRef<number>
    filterChips: ComputedRef<FilterChip[]>
}

export function useReadyToGoRingsFilters(
    props: Props,
    filters: Readonly<Ref<ReadyToGoRingFiltersFragment | undefined>>
): ReadyToGoRingsFilters {
    const { countryCode } = useCountryPlugin()
    const currencyInput = useCurrencyInput()

    const filterInput = computed<CatalogueReadyToGoRingsFilterInput>(() => ({
        primaryMetalCodes: props.metal ? [props.metal] : [],
        price: {
            country: countryCode.value!,
            currency: currencyInput.value,
            min: props.priceMin ? String(props.priceMin) : null,
            max: props.priceMax ? String(props.priceMax) : null,
        },
        readyToShip: props.readyToShip,
    }))

    const routes = useRoutes()

    const routeReset = routes.readyToGoRings(
        undefined,
        undefined,
        undefined,
        undefined,
        props.readyToShip
    )

    const getRoutePrice = (values: InputRangeModel): ReadyToGoRingsRoute =>
        routes.readyToGoRings(
            props.metal,
            Number(values[0]),
            Number(values[1]),
            undefined,
            props.readyToShip,
            props.sortBy
        )

    const getRouteAddMetal = (metal: MetalCode): ReadyToGoRingsRoute =>
        routes.readyToGoRings(
            metal,
            props.priceMin,
            props.priceMax,
            undefined,
            props.readyToShip,
            props.sortBy
        )

    const getRouteRemoveMetal = (): ReadyToGoRingsRoute =>
        routes.readyToGoRings(
            undefined,
            props.priceMin,
            props.priceMax,
            undefined,
            props.readyToShip,
            props.sortBy
        )

    const getRouteSort = (sortBy: CatalogueSortOrder): ReadyToGoRingsRoute =>
        routes.readyToGoRings(
            props.metal,
            props.priceMin,
            props.priceMax,
            undefined,
            props.readyToShip,
            sortBy
        )

    const activeFilterCount = useFiltersCount(
        computed(() => [
            !!filterInput.value?.primaryMetalCodes?.length,
            !!(filterInput.value?.price?.min || filterInput.value?.price?.max),
        ])
    )

    const formattedPriceRange = useFormattedPriceRange(
        computed(() => ({
            min: { amount: props.priceMin },
            max: { amount: props.priceMax },
        }))
    )

    const filterChips = computed(() => {
        const chips: FilterChip[] = []

        if (formattedPriceRange.value) {
            chips.push({
                label: formattedPriceRange.value,
                getRouteRemove: () =>
                    routes.readyToGoRings(
                        props.metal,
                        undefined,
                        undefined,
                        undefined,
                        props.readyToShip,
                        props.sortBy
                    ),
            })
        }

        const metalCode = filterInput.value.primaryMetalCodes?.[0]

        if (metalCode) {
            chips.push({
                label:
                    filters.value?.primaryMetals.find(
                        (v) => v.metal.systemCode === metalCode
                    )?.metal.translation.name || systemCodeToWords(metalCode),
                getRouteRemove: getRouteRemoveMetal,
            })
        }

        return chips
    })

    return {
        filterInput,
        routeReset,
        getRoutePrice,
        getRouteAddMetal,
        getRouteRemoveMetal,
        getRouteSort,
        activeFilterCount,
        filterChips,
    }
}
